import { useId } from 'react';

/**
 * Spec 081a — a labelled text input or textarea with a live `n/limit` counter (red at the limit),
 * an inline error tied to the field by aria-describedby, and `dir`/`lang` set for Arabic input.
 */
export interface CountedFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  lang: 'en' | 'ar';
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  error?: string;
  testId?: string;
  /** Visually hide the label (it stays the accessible name), for compact row editors. */
  hideLabel?: boolean;
}

export default function CountedField({
  label, value, onChange, maxLength, lang, multiline, rows = 3, placeholder, required, error, testId, hideLabel,
}: CountedFieldProps) {
  const id = useId();
  const counterId = `${id}-count`;
  const errorId = `${id}-error`;
  const atLimit = value.length >= maxLength;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const shared = {
    id,
    value,
    dir,
    lang,
    maxLength,
    placeholder,
    required,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${errorId} ${counterId}` : counterId,
    'data-testid': testId,
  } as const;

  return (
    <div className={`svc-field${error ? ' svc-field--error' : ''}`}>
      <label htmlFor={id} className={hideLabel ? 'svc-visually-hidden' : undefined}>
        {label}
        {required && <span className="svc-field__req" aria-hidden="true"> *</span>}
      </label>
      {multiline ? (
        <textarea {...shared} rows={rows} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...shared} type="text" onChange={(e) => onChange(e.target.value)} />
      )}
      <div className="svc-field__meta">
        {error ? <span id={errorId} className="svc-field__error" role="alert">{error}</span> : <span />}
        <span id={counterId} className={`svc-field__counter${atLimit ? ' svc-field__counter--limit' : ''}`}>
          {value.length}/{maxLength}
        </span>
      </div>
    </div>
  );
}
